import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRequestEmailChange } from "@/lib/auth-me";
import { useToast } from "@/components/v2/Toast";

/**
 * Linear-style email change. The dialog kicks off the request — the
 * actual swap only happens after the user clicks the verification link
 * delivered to the NEW address. Until then, sign-in continues to work
 * with the old email.
 */
export function ChangeEmailDialog({
  open,
  onClose,
  currentEmail,
}: {
  open: boolean;
  onClose: () => void;
  currentEmail: string;
}) {
  const request = useRequestEmailChange();
  const toast = useToast();
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setNewEmail("");
    setPassword("");
    setError(null);
  };

  const onSubmit = async () => {
    setError(null);
    try {
      await request.mutateAsync({ newEmail: newEmail.trim(), password });
      toast.success(
        "Verification email sent",
        `Click the link we sent to ${newEmail.trim()} to confirm.`,
      );
      reset();
      onClose();
    } catch (e) {
      setError(humanError((e as Error).message));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change email address</DialogTitle>
          <DialogDescription>
            Currently signed in as <strong>{currentEmail}</strong>. We'll send
            a verification link to your new email — your current address keeps
            working until you click it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="new-email">New email</Label>
            <Input
              id="new-email"
              type="email"
              autoComplete="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@workspace.com"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="email-password">Password</Label>
            <Input
              id="email-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!newEmail.trim() || !password || request.isPending}
          >
            {request.isPending ? "Sending…" : "Send verification email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function humanError(code: string): string {
  switch (code) {
    case "invalid_credentials":
      return "Password is incorrect.";
    case "invalid_email":
      return "Enter a valid email address.";
    case "same_email":
      return "That's already your current email.";
    case "email_taken":
      return "Someone else in this workspace has that email.";
    default:
      return code || "Couldn't request email change.";
  }
}
